/**
 * Route guards.
 *
 * These exist so a user is never *routed* somewhere the API would refuse. They
 * are not the access control — that is the API's `require_permission`, which
 * re-decides on every call. What these prevent is the worse experience: a
 * clickable destination that opens onto a 403.
 *
 * The pairing with navigation matters. A module the user cannot reach is absent
 * from the sidebar (the server filters the manifest), so these guards catch only
 * the remaining routes into a page — a bookmark, a pasted link, a redirect after
 * a scope change that narrowed what the user holds.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { ServiceDisabled, Forbidden, LoadingState, NotTheActiveService } from '../components/states';
import { useActiveClientService } from '../components/shell/service-switcher';
import { narrowsTo } from './narrows-to';
import { useCan, useHasService, useSession, type ClientService } from './session';

/** Requires a signed-in session; otherwise sends the user to sign in. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useSession((state) => state.status);
  const location = useLocation();

  if (status === 'unknown' || status === 'loading') {
    return <LoadingState label="Restoring your session" />;
  }
  if (status === 'anonymous') {
    // Where they were going is carried through, so signing in lands them there
    // rather than on a dashboard they then have to navigate away from.
    return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

/**
 * Requires the user to have finished activating.
 *
 * An invited employee who has not set a password may sign in — so they can see
 * their own status and change it — and may reach nothing else. Placed inside
 * `RequireAuth` so the redirect target is a page they can actually load.
 */
export function RequireActivated({ children }: { children: ReactNode }) {
  const user = useSession((state) => state.user);
  if (user?.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }
  return <>{children}</>;
}

/**
 * Requires a permission. Several means any one is enough.
 *
 * Renders the forbidden state rather than redirecting: a redirect from a pasted
 * link looks like the link was wrong, when the truth is that the user's access
 * does not cover it — and that is worth saying.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: string | string[];
  children: ReactNode;
}) {
  const allowed = useCan(permission);
  return allowed ? <>{children}</> : <Forbidden />;
}

/**
 * Requires the installation to have a service enabled.
 *
 * Separate from {@link RequirePermission} because the remedy is different, and
 * saying which one is the whole point: a missing permission is a conversation
 * with an administrator about a role, and a disabled service is a switch on the
 * Services screen. Reporting one as the other sends somebody to edit a role that
 * was never the problem.
 */
export function RequireService({
  service,
  children,
}: {
  service: string;
  children: ReactNode;
}) {
  const enabled = useHasService(service);
  return enabled ? <>{children}</> : <ServiceDisabled service={service} />;
}

/**
 * Both checks, in the order the server applies them.
 *
 * The service first, so a disabled service never presents as a missing
 * permission — the same ordering as `app/service_switch/guard.py`, and for the
 * same reason.
 */
export function Guarded({
  service,
  feature,
  permission,
  children,
}: {
  service?: string;
  /**
   * A feature the working service must have — `chat`, which only a WhatsApp
   * service does — as the page's module declares. Offers only services that
   * have it, so Chats never suggests switching to an SMS service.
   */
  feature?: string;
  permission?: string | string[];
  children: ReactNode;
}) {
  const serviceOk = useHasService(service);
  const permissionOk = useCan(permission);
  // Which of the client's services the portal is narrowed to. Null for a staff
  // session and for a client with nothing to choose between, and then this
  // check does nothing.
  const { services, active, choose } = useActiveClientService();

  if (service && !serviceOk) return <ServiceDisabled service={service} />;
  if (permission && !permissionOk) return <Forbidden />;
  // A page of a service the client holds but is not working in. Hiding its tab
  // was never the mechanism — a pasted link or a bookmark still arrived here,
  // and the page then loaded under a tab strip that did not show it.
  const fits = (entry: ClientService) =>
    entry.service === service && (!feature || (entry.features ?? []).includes(feature));
  if (service && narrowsTo(services, service) && active && !fits(active)) {
    return (
      <NotTheActiveService
        activeLabel={active.label}
        options={services.filter(fits)}
        onChoose={choose}
      />
    );
  }
  return <>{children}</>;
}

/**
 * Renders `children` only if the user holds the permission, with no fallback.
 *
 * For an affordance inside a page — a Delete button, a bulk action. **Absent,
 * not disabled**: a disabled control invites somebody to work out why, and the
 * answer is never on screen. If they cannot do it, it is not there.
 */
export function Can({
  permission,
  service,
  children,
  fallback = null,
}: {
  permission?: string | string[];
  service?: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const permissionOk = useCan(permission);
  const serviceOk = useHasService(service);
  return permissionOk && serviceOk ? <>{children}</> : <>{fallback}</>;
}

/** Redirects a signed-in user away from the sign-in page. */
export function RedirectIfSignedIn({ to = '/' }: { to?: string }) {
  const status = useSession((state) => state.status);
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;

  if (status === 'unknown' || status === 'loading') {
    return <LoadingState label="Checking your session" />;
  }
  if (status === 'authenticated') return <Navigate to={from ?? to} replace />;
  return null;
}
